import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HelperPopup } from '~/components/ui/HelperPopup';
import { TaskInput } from '~/generated/graphql';
import { move } from '~/helpers/array';
import { useSortable } from '~/hooks/useSortable';
import { TaskTypesSelect } from './ManageTasks';

export function TaskList({
  tasks,
  onClick,
  onSort,
  onDelete,
  onTaskTypeUpdate,
  isEditMode = false,
  isTaskTypeEditable,
  templateBehavior,
}: {
  tasks: TaskInput[];
  onClick: (id: string) => void;
  onSort: (tasks: TaskInput[]) => void;
  onDelete: (id: string) => void;
  onTaskTypeUpdate: (id: string, type: string) => void;
  isEditMode?: boolean;
  isTaskTypeEditable?: (task: TaskInput) => boolean;
  templateBehavior?: 'static' | 'asset_rules';
}) {
  // const { t } = useTranslation(['translation', 'job']);
  const sortRef = useRef<HTMLDivElement>(null);

  const handleSortUpdate = useCallback(
    ({ oldIndex, newIndex }: { oldIndex?: number; newIndex?: number }) => {
      if (oldIndex === undefined || newIndex === undefined) {
        return;
      }
      onSort(move(tasks, oldIndex, newIndex));
    },
    [onSort, tasks]
  );

  useSortable(sortRef?.current, {
    onUpdate: handleSortUpdate,
  });

  return (
    <div ref={sortRef} className='bg-white'>
      {tasks.map((task, i) => {
        // Determine if the task type can be edited

        let canEditTaskType = true;

        if (isTaskTypeEditable) {
          // Use the provided function if available
          canEditTaskType = isTaskTypeEditable(task);
        } else {
          // Fallback to our previous logic if no function provided
          const isNewTask = !task.id.includes(':');
          canEditTaskType =
            !isEditMode || isNewTask || task.type === 'Standard';
        }

        return (
          <TaskEditCard
            key={`${task.id}-${i}`}
            onClick={onClick}
            task={task}
            onDelete={onDelete}
            onTaskTypeUpdate={onTaskTypeUpdate}
            canEditTaskType={canEditTaskType}
            templateBehavior={templateBehavior}
          />
        );
      })}
    </div>
  );
}

type TaskEditCardProps = {
  task: TaskInput;
  onClick: (taskId: string) => void;
  onDelete: (id: string) => void;
  onTaskTypeUpdate: (id: string, type: string) => void;
  canEditTaskType?: boolean;
  templateBehavior?: 'static' | 'asset_rules';
};

function TaskEditCard({
  task,
  onClick,
  onDelete,
  onTaskTypeUpdate,
  canEditTaskType = true,
  templateBehavior,
}: TaskEditCardProps) {
  const { t } = useTranslation(['translation', 'job']);

  // Get asset types for display (for rules-based templates with Asset tasks)
  const assetTypes = useMemo(() => {
    if (
      templateBehavior === 'asset_rules' &&
      task.type === 'Asset' &&
      task.asset?.assetTypes &&
      task.asset.assetTypes.length > 0
    ) {
      return task.asset.assetTypes;
    }
    return null;
  }, [templateBehavior, task.type, task.asset?.assetTypes]);

  // Check if this is a default rule (assetTypes must be undefined, not empty array)
  // Empty array means user hasn't selected types yet, undefined means default rule
  const isDefaultRule = useMemo(() => {
    return (
      templateBehavior === 'asset_rules' &&
      task.type === 'Asset' &&
      task.asset?.selection === 'all' &&
      task.asset?.assetTypes === undefined // Only undefined, not empty array
    );
  }, [
    templateBehavior,
    task.type,
    task.asset?.selection,
    task.asset?.assetTypes,
  ]);

  // Format asset types for display (truncated)
  const assetTypesDisplay = useMemo(() => {
    if (!assetTypes) return null;
    const maxLength = 30; // Maximum characters to display
    const joined = assetTypes.join(', ');
    if (joined.length <= maxLength) {
      return joined;
    }
    // Truncate and add ellipsis
    return joined.substring(0, maxLength - 3) + '...';
  }, [assetTypes]);

  // Don't display a task if it's set to be deleted on save
  if (task._destroy) {
    return null;
  }

  return (
    <div className='mb-3 flex w-full items-center gap-3'>
      <div className='relative w-full'>
        <div
          className={classNames(
            'flex flex-1 items-center rounded border bg-white text-grey-20 shadow transition-all hover:text-grey-40 hover:shadow-md',
            `${task.required ? 'border-brand' : 'border-grey-10'}`
          )}
          onClick={() => onClick(task.id)}
        >
          <div className='handle flex flex-1 cursor-pointer p-2.5'>
            <div className='flex flex-1 items-center gap-2 pr-[35px]'>
              <p className='cursor-move text-primary'>
                {task.name ??
                  (task.type === 'Item'
                    ? t('job:tasks.addItems')
                    : t(task.type))}
              </p>
              {assetTypesDisplay && (
                <span className='truncate text-secondary'>
                  ({assetTypesDisplay})
                </span>
              )}
              {isDefaultRule && (
                <HelperPopup>{t('job:tasks.defaultRuleHelp')}</HelperPopup>
              )}
            </div>
            {task.type === 'Restock' &&
              task.item &&
              !task.item.locationId.startsWith('Elsewhere') &&
              !task.item.locationId && (
                <p className='relative -top-4 h-4 w-4'>
                  <HelperPopup>⚠️ Missing source location</HelperPopup>
                </p>
              )}
          </div>
          {task.required && (
            <div className='absolute -right-2 -top-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand'>
              <span className='text-[9px] font-bold text-white'>M</span>
            </div>
          )}
        </div>
        <div className='absolute right-2 top-0 flex h-full items-center justify-center'>
          <TaskTypesSelect
            type={task.type}
            onTypeSelect={(type) => onTaskTypeUpdate(task.id, type)}
            canEditTaskType={canEditTaskType && !isDefaultRule}
            isDefaultRuleTask={isDefaultRule}
          />
        </div>
      </div>
      <QuickDeleteTaskButton taskId={task.id} onDelete={onDelete} />
    </div>
  );
}

type QuickDeleteTaskButtonProps = {
  taskId: string;
  onDelete: (id: string) => void;
};

function QuickDeleteTaskButton({
  taskId,
  onDelete,
}: QuickDeleteTaskButtonProps) {
  return (
    <div
      onClick={() => onDelete(taskId)}
      className='my-1 flex h-8 w-[50px] cursor-pointer items-center justify-center rounded-3xl border border-grey-10 text-grey-20 shadow transition-all hover:text-red-600'
    >
      <FontAwesomeIcon className='h-5 w-4' icon={faTrashCan} />
    </div>
  );
}
